import { useState, useEffect, useRef } from "react"
import { useAuth } from "../hooks/useAuth"
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"
import jsPDF from "jspdf"
import html2canvas from "html2canvas"

// 페이브텍 고정 정보 (모든 견적서에 표시)
const COMPANY_INFO = {
  name: "페이브텍주식회사",
  nameEn: "Pave Tech Co.,Ltd.",
  address: "경기 화성시 봉담읍 수기웃말길 22-5",
  tel: "031-273-5325",
  fax: "031-222-5325",
  logoUrl: "https://static.lumi.new/a6/a63d536d75bc6d80fe8261fd7f08abe8.webp"
}

interface QuotationItem {
  name: string
  description: string
  amount: number
  quantity?: number
  unitPrice?: number
  remark?: string
}

interface Quotation {
  _id?: string
  quotationName: string
  customerName: string
  customerCompany?: string
  customerPhone?: string
  customerFax?: string
  quotationNumber?: string
  quotationDate?: string
  deliveryMonths?: number
  materialItems: QuotationItem[]
  laborItems: QuotationItem[]
  materialTotal: number
  laborTotal: number
  profitRate: number
  profitAmount: number
  expenseRate: number
  expenseAmount: number
  totalAmount: number
  creator: string
  creatorName: string
  creatorTeam: string
  createdAt: string
  updatedAt: string
  status: "draft" | "completed" | "sent"
  sentTo?: string
  sentDate?: string
  notes?: string
  actualCost?: string
}

const emptyItem = (): QuotationItem => ({ name: "", description: "", amount: 0, quantity: 1, unitPrice: 0, remark: "" })

// 항목 합계 = 수량 x 단가
function calcItemAmount(item: QuotationItem): number {
  const qty = Number(item.quantity) || 0
  const price = Number(item.unitPrice) || 0
  return qty * price
}

// 숫자를 한글 금액으로 변환 (예: 170000000 -> "일억 칠천만원")
function numberToKorean(num: number): string {
  if (!num || num <= 0) return "영원"
  const units = ["", "만", "억", "조", "경"]
  const small = ["", "일", "이", "삼", "사", "오", "육", "칠", "팔", "구"]
  const pos = ["", "십", "백", "천"]
  let result = ""
  let groupIndex = 0
  let n = Math.floor(num)
  while (n > 0) {
    const group = n % 10000
    if (group > 0) {
      let groupStr = ""
      const digits = String(group).padStart(4, "0").split("").map(Number)
      for (let i = 0; i < 4; i++) {
        const d = digits[i]
        if (d > 0) {
          groupStr += small[d] + pos[3 - i]
        }
      }
      result = groupStr + units[groupIndex] + " " + result
    }
    n = Math.floor(n / 10000)
    groupIndex++
  }
  return "일금 " + result.trim() + "원"
}

// 견적번호 생성: PTQ-YYMMDD-NN (같은 날짜 내 순번 자동 증가)
function generateQuotationNumber(existing: Quotation[]): string {
  const now = new Date()
  const yy = String(now.getFullYear()).slice(2)
  const mm = String(now.getMonth() + 1).padStart(2, "0")
  const dd = String(now.getDate()).padStart(2, "0")
  const datePart = `${yy}${mm}${dd}`
  const prefix = `PTQ-${datePart}-`
  let maxSeq = 0
  existing.forEach(q => {
    if (q.quotationNumber && q.quotationNumber.startsWith(prefix)) {
      const seq = parseInt(q.quotationNumber.slice(prefix.length), 10)
      if (!isNaN(seq) && seq > maxSeq) maxSeq = seq
    }
  })
  return `${prefix}${String(maxSeq + 1).padStart(2, "0")}`
}

export function QuotationManagement() {
  const { user, userName, teamName, isManager, isCLevel, isAdmin } = useAuth()
  const hasAccess = isManager || isCLevel || isAdmin

  const [quotations, setQuotations] = useState<Quotation[]>([])
  const [loading, setLoading] = useState(false)
  const [showEditor, setShowEditor] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [searchKeyword, setSearchKeyword] = useState("")
  const pdfRef = useRef<HTMLDivElement>(null)
  const [pdfTarget, setPdfTarget] = useState<Quotation | null>(null)

  // Form fields
  const [quotationName, setQuotationName] = useState("")
  const [customerName, setCustomerName] = useState("")
  const [customerCompany, setCustomerCompany] = useState("")
  const [customerPhone, setCustomerPhone] = useState("")
  const [customerFax, setCustomerFax] = useState("")
  const [quotationNumber, setQuotationNumber] = useState("")
  const [quotationDate, setQuotationDate] = useState("")
  const [deliveryMonths, setDeliveryMonths] = useState<number>(2)
  const [materialItems, setMaterialItems] = useState<QuotationItem[]>([emptyItem()])
  const [laborItems, setLaborItems] = useState<QuotationItem[]>([emptyItem()])
  const [profitRate, setProfitRate] = useState<number>(0)
  const [expenseRate, setExpenseRate] = useState<number>(0)
  const [sentTo, setSentTo] = useState("")
  const [sentDate, setSentDate] = useState("")
  const [notes, setNotes] = useState("")
  const [actualCost, setActualCost] = useState("")
  const [status, setStatus] = useState<"draft" | "completed" | "sent">("draft")

  useEffect(() => {
    if (hasAccess) loadQuotations()
  }, [hasAccess])

  // ESC 키로 모달 닫기
  useEffect(() => {
    if (!showEditor) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeEditor()
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [showEditor])

  function closeEditor() {
    setShowEditor(false)
    resetForm()
  }

  async function loadQuotations() {
    setLoading(true)
    try {
      const { list } = await lumi.entities.quotations.list({
        sort: { createdAt: -1 }
      })
      setQuotations(list || [])
    } catch (e) {
      console.error(e)
      toast.error("견적서 목록을 불러오지 못했습니다")
    } finally {
      setLoading(false)
    }
  }

  function resetForm() {
    setQuotationName("")
    setCustomerName("")
    setCustomerCompany("")
    setCustomerPhone("")
    setCustomerFax("")
    setQuotationNumber("")
    setQuotationDate("")
    setDeliveryMonths(2)
    setMaterialItems([emptyItem()])
    setLaborItems([emptyItem()])
    setProfitRate(0)
    setExpenseRate(0)
    setSentTo("")
    setSentDate("")
    setNotes("")
    setActualCost("")
    setStatus("draft")
    setEditingId(null)
  }

  function openNewEditor() {
    resetForm()
    // 신규 작성 시 견적번호/견적일 자동 부여
    setQuotationNumber(generateQuotationNumber(quotations))
    setQuotationDate(new Date().toISOString().slice(0, 10))
    setShowEditor(true)
  }

  function openEditEditor(q: Quotation) {
    setQuotationName(q.quotationName)
    setCustomerName(q.customerName)
    setCustomerCompany(q.customerCompany || "")
    setCustomerPhone(q.customerPhone || "")
    setCustomerFax(q.customerFax || "")
    setQuotationNumber(q.quotationNumber || generateQuotationNumber(quotations))
    setQuotationDate(q.quotationDate ? q.quotationDate.slice(0, 10) : new Date().toISOString().slice(0, 10))
    setDeliveryMonths(typeof q.deliveryMonths === "number" ? q.deliveryMonths : 2)
    setMaterialItems(q.materialItems?.length ? q.materialItems : [emptyItem()])
    setLaborItems(q.laborItems?.length ? q.laborItems : [emptyItem()])
    setProfitRate(q.profitRate || 0)
    setExpenseRate(q.expenseRate || 0)
    setSentTo(q.sentTo || "")
    setSentDate(q.sentDate ? q.sentDate.slice(0, 10) : "")
    setNotes(q.notes || "")
    setActualCost((q as any).actualCost || "")
    setStatus(q.status || "draft")
    setEditingId(q._id || null)
    setShowEditor(true)
  }

  // 합계 계산 (각 항목 = 수량 x 단가)
  const materialTotal = materialItems.reduce((s, i) => s + calcItemAmount(i), 0)
  const laborTotal = laborItems.reduce((s, i) => s + calcItemAmount(i), 0)
  const baseTotal = materialTotal + laborTotal
  // 향후 수식 제공 예정 - 현재는 단순 % 기준 계산 (저장 시 수식 업데이트 필요)
  const profitAmount = Math.round(baseTotal * (Number(profitRate) || 0) / 100)
  const expenseAmount = Math.round(baseTotal * (Number(expenseRate) || 0) / 100)
  const totalAmount = baseTotal + profitAmount + expenseAmount

  function addMaterialItem() {
    if (materialItems.length >= 10) {
      toast.error("재료비는 최대 10개까지 추가 가능합니다")
      return
    }
    setMaterialItems([...materialItems, emptyItem()])
  }

  function removeMaterialItem(idx: number) {
    if (materialItems.length === 1) return
    setMaterialItems(materialItems.filter((_, i) => i !== idx))
  }

  function addLaborItem() {
    if (laborItems.length >= 10) {
      toast.error("인건비는 최대 10개까지 추가 가능합니다")
      return
    }
    setLaborItems([...laborItems, emptyItem()])
  }

  function removeLaborItem(idx: number) {
    if (laborItems.length === 1) return
    setLaborItems(laborItems.filter((_, i) => i !== idx))
  }

  function updateMaterialItem(idx: number, field: keyof QuotationItem, value: string | number) {
    setMaterialItems(materialItems.map((it, i) => {
      if (i !== idx) return it
      const next = { ...it, [field]: value }
      next.amount = calcItemAmount(next)
      return next
    }))
  }

  function updateLaborItem(idx: number, field: keyof QuotationItem, value: string | number) {
    setLaborItems(laborItems.map((it, i) => {
      if (i !== idx) return it
      const next = { ...it, [field]: value }
      next.amount = calcItemAmount(next)
      return next
    }))
  }

  async function handleSave() {
    if (!user) {
      toast.error("로그인이 필요합니다")
      return
    }
    if (!quotationName.trim()) {
      toast.error("견적서 이름을 입력해주세요")
      return
    }
    if (!customerName.trim()) {
      toast.error("고객 이름을 입력해주세요")
      return
    }

    const now = new Date().toISOString()
    const payload: Omit<Quotation, "_id"> = {
      quotationName: quotationName.trim(),
      customerName: customerName.trim(),
      customerCompany: customerCompany.trim(),
      customerPhone: customerPhone.trim(),
      customerFax: customerFax.trim(),
      quotationNumber: quotationNumber.trim() || generateQuotationNumber(quotations),
      quotationDate: quotationDate ? new Date(quotationDate).toISOString() : now,
      deliveryMonths: Number(deliveryMonths) || 0,
      materialItems: materialItems
        .map(i => ({ ...i, amount: calcItemAmount(i) }))
        .filter(i => i.name.trim() || i.amount > 0),
      laborItems: laborItems
        .map(i => ({ ...i, amount: calcItemAmount(i) }))
        .filter(i => i.name.trim() || i.amount > 0),
      materialTotal,
      laborTotal,
      profitRate: Number(profitRate) || 0,
      profitAmount,
      expenseRate: Number(expenseRate) || 0,
      expenseAmount,
      totalAmount,
      creator: user.userId,
      creatorName: userName || user.userName || "",
      creatorTeam: teamName || "",
      createdAt: now,
      updatedAt: now,
      status,
      sentTo: sentTo.trim(),
      sentDate: sentDate ? new Date(sentDate).toISOString() : "",
      notes: notes.trim(),
      actualCost: actualCost.trim()
    }

    try {
      if (editingId) {
        const existing = quotations.find(q => q._id === editingId)
        await lumi.entities.quotations.update(editingId, {
          ...payload,
          createdAt: existing?.createdAt || now,
          creator: existing?.creator || user.userId,
          creatorName: existing?.creatorName || payload.creatorName,
          creatorTeam: existing?.creatorTeam || payload.creatorTeam
        })
        toast.success("견적서가 수정되었습니다")
      } else {
        await lumi.entities.quotations.create(payload)
        toast.success("견적서가 저장되었습니다")
      }
      setShowEditor(false)
      resetForm()
      await loadQuotations()
    } catch (e) {
      console.error(e)
      toast.error("저장 실패")
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("정말 삭제하시겠습니까?")) return
    try {
      await lumi.entities.quotations.delete(id)
      toast.success("삭제되었습니다")
      await loadQuotations()
    } catch (e) {
      console.error(e)
      toast.error("삭제 실패")
    }
  }

  async function handleDownloadPDF(q: Quotation) {
    setPdfTarget(q)
    toast.loading("PDF 생성 중...", { id: "pdf" })
    // 다음 렌더 후 + 이미지(로고) 로딩 완료 후 PDF 생성
    setTimeout(async () => {
      if (!pdfRef.current) {
        toast.error("PDF 생성 실패", { id: "pdf" })
        setPdfTarget(null)
        return
      }
      try {
        // 영역 내 모든 이미지(로고) 로딩 대기
        const imgs = Array.from(pdfRef.current.querySelectorAll("img"))
        await Promise.all(
          imgs.map(
            (img) =>
              img.complete
                ? Promise.resolve()
                : new Promise<void>((resolve) => {
                    img.onload = () => resolve()
                    img.onerror = () => resolve()
                  })
          )
        )
        const canvas = await html2canvas(pdfRef.current, {
          scale: 2,
          backgroundColor: "#ffffff",
          useCORS: true,
          allowTaint: false,
          logging: false
        })
        const imgData = canvas.toDataURL("image/png")
        const pdf = new jsPDF("p", "mm", "a4")
        const pdfWidth = pdf.internal.pageSize.getWidth()
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width
        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight)
        pdf.save(`견적서_${q.quotationNumber || q.quotationName}_${q.customerName}.pdf`)
        toast.success("PDF 다운로드 완료", { id: "pdf" })
      } catch (e) {
        console.error(e)
        toast.error("PDF 생성 실패", { id: "pdf" })
      } finally {
        setPdfTarget(null)
      }
    }, 500)
  }

  const filtered = quotations.filter(q =>
    !searchKeyword ||
    q.quotationName?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    q.customerName?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    q.creatorName?.toLowerCase().includes(searchKeyword.toLowerCase())
  )

  // 권한 확인
  if (!hasAccess) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-8">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full mx-auto mb-4 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">접근 권한 없음</h2>
          <p className="text-gray-600">견적서 관리는 팀장급 이상만 사용할 수 있습니다.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">견적서 관리</h1>
          <p className="text-sm text-gray-500 mt-1">견적서 작성 및 PDF 다운로드, 이력 관리</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadQuotations}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
          >
            🔄 새로고침
          </button>
          <button
            onClick={openNewEditor}
            className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors shadow-sm"
          >
            + 견적서 작성
          </button>
        </div>
      </div>

      {/* 검색 */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
        <input
          type="text"
          placeholder="견적서 이름, 고객명, 작성자로 검색..."
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
        />
      </div>

      {/* 목록 */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">견적번호</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">견적서명</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">고객명</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">총 금액</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">작성자</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">작성일</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">전달기업</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">상태</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">작업</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan={9} className="text-center py-8 text-gray-500">로딩 중...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-8 text-gray-500">견적서가 없습니다</td></tr>
              ) : filtered.map(q => (
                <tr
                  key={q._id}
                  onClick={() => openEditEditor(q)}
                  className="hover:bg-orange-50 cursor-pointer transition-colors"
                  title="클릭하여 수정"
                >
                  <td className="px-4 py-3 text-sm font-mono text-gray-700">{q.quotationNumber || "-"}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">{q.quotationName}</td>
                  <td className="px-4 py-3 text-gray-700">
                    <div>{q.customerName}</div>
                    {q.customerCompany && <div className="text-xs text-gray-500">{q.customerCompany}</div>}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-orange-600 whitespace-nowrap">{(q.totalAmount || 0).toLocaleString()} 원</td>
                  <td className="px-4 py-3 text-gray-700">
                    <div>{q.creatorName}</div>
                    <div className="text-xs text-gray-500">{q.creatorTeam}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                    {q.createdAt ? new Date(q.createdAt).toLocaleDateString("ko-KR") : "-"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                    {q.sentTo || "-"}
                    {q.sentDate && (
                      <div className="text-xs text-gray-500">
                        {new Date(q.sentDate).toLocaleDateString("ko-KR")}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      q.status === "sent" ? "bg-green-100 text-green-800" :
                      q.status === "completed" ? "bg-blue-100 text-blue-800" :
                      "bg-gray-100 text-gray-800"
                    }`}>
                      {q.status === "sent" ? "전달완료" : q.status === "completed" ? "작성완료" : "임시저장"}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDownloadPDF(q) }}
                        className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold transition-colors"
                        title="견적서 PDF 출력"
                      >
                        🖨️ 출력
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); openEditEditor(q) }}
                        className="px-2 py-1 text-xs bg-orange-100 hover:bg-orange-200 text-orange-700 rounded transition-colors"
                      >
                        수정
                      </button>
                      {(q.creator === user?.userId || isAdmin) && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(q._id!) }}
                          className="px-2 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-700 rounded transition-colors"
                        >
                          삭제
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 작성/수정 모달 */}
      {showEditor && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={closeEditor}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-8 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                {editingId ? "견적서 수정" : "견적서 작성"}
              </h2>
              <button
                onClick={closeEditor}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* 견적서 정보 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">견적서 이름 *</label>
                  <input
                    value={quotationName}
                    onChange={(e) => setQuotationName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    placeholder="예: Pilot 소형용접 시스템 개발"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">견적번호 (자동부여)</label>
                  <input
                    value={quotationNumber}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-200 bg-gray-50 text-gray-600 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">견적일 (자동부여)</label>
                  <input
                    type="date"
                    value={quotationDate}
                    onChange={(e) => setQuotationDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">납품일 (발주 후 N개월 이내)</label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">발주 후</span>
                    <input
                      type="number"
                      min={1}
                      value={deliveryMonths || ""}
                      onChange={(e) => setDeliveryMonths(Number(e.target.value))}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-center"
                      placeholder="2"
                    />
                    <span className="text-sm text-gray-500">개월 이내</span>
                  </div>
                </div>
              </div>

              {/* 고객 정보 */}
              <div className="border border-gray-200 rounded-xl p-4">
                <h3 className="font-bold text-gray-900 mb-3">🏢 고객 정보</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">고객사명</label>
                    <input
                      value={customerCompany}
                      onChange={(e) => setCustomerCompany(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      placeholder="예: 알티스"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">고객명 (담당자) *</label>
                    <input
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      placeholder="예: 권순관 이사"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">전화번호</label>
                    <input
                      value={customerPhone}
                      onChange={(e) => setCustomerPhone(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      placeholder="예: 032-710-7679"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">팩스번호</label>
                    <input
                      value={customerFax}
                      onChange={(e) => setCustomerFax(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      placeholder="예: 032-232-7679"
                    />
                  </div>
                </div>
              </div>

              {/* 전달 정보 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">전달 기업명</label>
                  <input
                    value={sentTo}
                    onChange={(e) => setSentTo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    placeholder="전달된 기업 (선택)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">전달일</label>
                  <input
                    type="date"
                    value={sentDate}
                    onChange={(e) => setSentDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>

              {/* 재료비 */}
              <div className="border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-gray-900">📦 재료비 (최대 10개)</h3>
                  <button
                    onClick={addMaterialItem}
                    className="px-3 py-1 text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg"
                  >
                    + 항목 추가
                  </button>
                </div>
                <div className="hidden sm:grid grid-cols-12 gap-2 px-1 mb-1 text-xs font-semibold text-gray-500">
                  <div className="col-span-3">항목명</div>
                  <div className="col-span-4">세부 내용</div>
                  <div className="col-span-2 text-right">수량</div>
                  <div className="col-span-2 text-right">단가</div>
                  <div className="col-span-1"></div>
                </div>
                <div className="space-y-2">
                  {materialItems.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-start">
                      <input
                        value={item.name}
                        onChange={(e) => updateMaterialItem(idx, "name", e.target.value)}
                        placeholder="항목명"
                        className="col-span-6 sm:col-span-3 px-2 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                      <textarea
                        value={item.description}
                        onChange={(e) => updateMaterialItem(idx, "description", e.target.value)}
                        placeholder={"세부 내용 (엔터로 줄바꿈)\n예) - AVC 모듈\n- OSC 모듈\n- 직선형 토치"}
                        rows={3}
                        className="col-span-6 sm:col-span-4 px-2 py-2 border border-gray-300 rounded-lg text-sm resize-y"
                      />
                      <input
                        type="number"
                        min={0}
                        value={item.quantity ?? ""}
                        onChange={(e) => updateMaterialItem(idx, "quantity", Number(e.target.value))}
                        placeholder="수량"
                        className="col-span-4 sm:col-span-2 px-2 py-2 border border-gray-300 rounded-lg text-sm text-right"
                      />
                      <input
                        type="number"
                        min={0}
                        value={item.unitPrice ?? ""}
                        onChange={(e) => updateMaterialItem(idx, "unitPrice", Number(e.target.value))}
                        placeholder="단가"
                        className="col-span-6 sm:col-span-2 px-2 py-2 border border-gray-300 rounded-lg text-sm text-right"
                      />
                      <button
                        onClick={() => removeMaterialItem(idx)}
                        disabled={materialItems.length === 1}
                        className="col-span-2 sm:col-span-1 text-red-500 hover:text-red-700 disabled:text-gray-300 pt-2"
                      >
                        ✕
                      </button>
                      <div className="col-span-12 text-right text-xs text-gray-600 -mt-1">
                        합계: <span className="font-semibold text-gray-800">{calcItemAmount(item).toLocaleString()} 원</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="text-right mt-3 font-bold text-gray-900">
                  재료비 합계: <span className="text-blue-600">{materialTotal.toLocaleString()} 원</span>
                </div>
              </div>

              {/* 인건비 */}
              <div className="border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-gray-900">👷 인건비 (최대 10개)</h3>
                  <button
                    onClick={addLaborItem}
                    className="px-3 py-1 text-sm bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded-lg"
                  >
                    + 항목 추가
                  </button>
                </div>
                <div className="hidden sm:grid grid-cols-12 gap-2 px-1 mb-1 text-xs font-semibold text-gray-500">
                  <div className="col-span-3">항목명</div>
                  <div className="col-span-4">세부 내용</div>
                  <div className="col-span-2 text-right">수량</div>
                  <div className="col-span-2 text-right">단가</div>
                  <div className="col-span-1"></div>
                </div>
                <div className="space-y-2">
                  {laborItems.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-start">
                      <input
                        value={item.name}
                        onChange={(e) => updateLaborItem(idx, "name", e.target.value)}
                        placeholder="항목명"
                        className="col-span-6 sm:col-span-3 px-2 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                      <textarea
                        value={item.description}
                        onChange={(e) => updateLaborItem(idx, "description", e.target.value)}
                        placeholder={"세부 내용 (엔터로 줄바꿈)\n예) - 사양검토\n- 장비 설계\n- 개발"}
                        rows={3}
                        className="col-span-6 sm:col-span-4 px-2 py-2 border border-gray-300 rounded-lg text-sm resize-y"
                      />
                      <input
                        type="number"
                        min={0}
                        value={item.quantity ?? ""}
                        onChange={(e) => updateLaborItem(idx, "quantity", Number(e.target.value))}
                        placeholder="수량"
                        className="col-span-4 sm:col-span-2 px-2 py-2 border border-gray-300 rounded-lg text-sm text-right"
                      />
                      <input
                        type="number"
                        min={0}
                        value={item.unitPrice ?? ""}
                        onChange={(e) => updateLaborItem(idx, "unitPrice", Number(e.target.value))}
                        placeholder="단가"
                        className="col-span-6 sm:col-span-2 px-2 py-2 border border-gray-300 rounded-lg text-sm text-right"
                      />
                      <button
                        onClick={() => removeLaborItem(idx)}
                        disabled={laborItems.length === 1}
                        className="col-span-2 sm:col-span-1 text-red-500 hover:text-red-700 disabled:text-gray-300 pt-2"
                      >
                        ✕
                      </button>
                      <div className="col-span-12 text-right text-xs text-gray-600 -mt-1">
                        합계: <span className="font-semibold text-gray-800">{calcItemAmount(item).toLocaleString()} 원</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="text-right mt-3 font-bold text-gray-900">
                  인건비 합계: <span className="text-emerald-600">{laborTotal.toLocaleString()} 원</span>
                </div>
              </div>

              {/* 영업이익 / 경비 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-gray-200 rounded-xl p-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    영업이익률 (%)
                    <span className="text-xs text-gray-500 ml-2">※ 향후 수식 적용 예정</span>
                  </label>
                  <input
                    type="number"
                    value={profitRate || ""}
                    onChange={(e) => setProfitRate(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    placeholder="0"
                  />
                  <div className="text-right mt-2 text-sm text-gray-600">
                    예상 금액: <span className="font-semibold">{profitAmount.toLocaleString()} 원</span>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-xl p-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    경비 및 제비용률 (%)
                    <span className="text-xs text-gray-500 ml-2">※ 향후 수식 적용 예정</span>
                  </label>
                  <input
                    type="number"
                    value={expenseRate || ""}
                    onChange={(e) => setExpenseRate(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    placeholder="0"
                  />
                  <div className="text-right mt-2 text-sm text-gray-600">
                    예상 금액: <span className="font-semibold">{expenseAmount.toLocaleString()} 원</span>
                  </div>
                </div>
              </div>

              {/* 총합 */}
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-300 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold text-gray-900">총 견적 금액</span>
                  <span className="text-3xl font-bold text-orange-600">{totalAmount.toLocaleString()} 원</span>
                </div>
                <div className="mt-2 text-xs text-gray-600 grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div>재료비: {materialTotal.toLocaleString()}</div>
                  <div>인건비: {laborTotal.toLocaleString()}</div>
                  <div>영업이익: {profitAmount.toLocaleString()}</div>
                  <div>경비: {expenseAmount.toLocaleString()}</div>
                </div>
              </div>

              {/* 실제 투입 비용 (내부 참고용, PDF 미노출) */}
              <div className="border border-dashed border-gray-300 bg-gray-50 rounded-xl p-4">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  실제 투입 비용
                  <span className="text-xs text-gray-500 ml-2">※ 내부 참고용 - PDF 출력 시 표시되지 않습니다</span>
                </label>
                <textarea
                  value={actualCost}
                  onChange={(e) => setActualCost(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 resize-y min-h-[80px]"
                  placeholder="예: 실제 소요된 재료비/인건비 등 (자유 입력, 엔터로 줄바꿈)"
                />
              </div>

              {/* 비고 / 상태 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">비고</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    placeholder="비고 입력 (선택)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">상태</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as any)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="draft">임시저장</option>
                    <option value="completed">작성완료</option>
                    <option value="sent">전달완료</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-2">
              <button
                onClick={closeEditor}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium"
              >
                취소
              </button>
              <button
                onClick={handleSave}
                className="px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-semibold shadow-sm"
              >
                {editingId ? "수정 저장" : "견적서 저장"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF 출력 영역 (화면에서 잠시 렌더링) */}
      {pdfTarget && (
        <div className="fixed -left-[9999px] top-0">
          <div ref={pdfRef} style={{ width: "794px", padding: "40px", background: "#fff", fontFamily: "'Malgun Gothic', sans-serif", color: "#111827" }}>
            {/* 제목 */}
            <h1 style={{ fontSize: "34px", fontWeight: "bold", textAlign: "center", letterSpacing: "24px", margin: "0 0 24px 0" }}>견 적 서</h1>

            {/* 헤더: 좌측 고객정보 / 우측 회사정보 */}
            <div style={{ display: "flex", justifyContent: "space-between", borderTop: "2px solid #111827", borderBottom: "2px solid #111827", padding: "14px 4px", marginBottom: "20px" }}>
              {/* 좌측: 고객 정보 */}
              <div style={{ fontSize: "13px", lineHeight: "1.9", width: "55%" }}>
                <div style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "4px" }}>
                  {pdfTarget.customerCompany || "-"} / {pdfTarget.customerName} 귀하
                </div>
                <div>TEL) {pdfTarget.customerPhone || "-"}</div>
                <div>FAX) {pdfTarget.customerFax || "-"}</div>
                <div style={{ marginTop: "6px", textDecoration: "underline" }}>
                  견 적 번 호 : {pdfTarget.quotationNumber || "-"}
                </div>
                <div style={{ marginTop: "6px" }}>
                  견 적 일 : {pdfTarget.quotationDate ? new Date(pdfTarget.quotationDate).toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric" }) : "-"}
                </div>
                <div>
                  납 품 일 : 발주 후 {pdfTarget.deliveryMonths || 0}개월 이내
                </div>
              </div>
              {/* 우측: 회사 정보 + 로고 + 직인 */}
              <div style={{ fontSize: "12px", lineHeight: "1.8", width: "45%", textAlign: "right", position: "relative" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px", marginBottom: "4px" }}>
                  <img src={COMPANY_INFO.logoUrl} crossOrigin="anonymous" style={{ height: "34px", objectFit: "contain" }} alt="logo" />
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontSize: "15px", fontWeight: "bold" }}>{COMPANY_INFO.name}</div>
                    <div style={{ fontSize: "12px", color: "#374151" }}>{COMPANY_INFO.nameEn}</div>
                  </div>
                </div>
                <div style={{ marginTop: "6px" }}>{COMPANY_INFO.address}</div>
                <div style={{ marginTop: "6px" }}>TEL) {COMPANY_INFO.tel}</div>
                <div>FAX) {COMPANY_INFO.fax}</div>
              </div>
            </div>

            {/* 총액 한글 표기 */}
            <div style={{ border: "1px solid #111827", padding: "10px 14px", marginBottom: "16px", fontSize: "15px", fontWeight: "bold" }}>
              합계 금액 : {numberToKorean(pdfTarget.totalAmount || 0)} (₩{(pdfTarget.totalAmount || 0).toLocaleString()})
            </div>

            {/* 재료비 */}
            <h3 style={{ fontSize: "15px", fontWeight: "bold", marginTop: "16px", marginBottom: "6px" }}>■ 재료비</h3>
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "14px", fontSize: "12px" }}>
              <thead>
                <tr style={{ background: "#F3F4F6" }}>
                  <th style={{ padding: "6px", border: "1px solid #111827", width: "7%" }}>No.</th>
                  <th style={{ padding: "6px", border: "1px solid #111827", width: "28%" }}>항목</th>
                  <th style={{ padding: "6px", border: "1px solid #111827" }}>세부 내용</th>
                  <th style={{ padding: "6px", border: "1px solid #111827", width: "22%" }}>금액 (원)</th>
                </tr>
              </thead>
              <tbody>
                {pdfTarget.materialItems?.map((item, i) => (
                  <tr key={i}>
                    <td style={{ padding: "6px", border: "1px solid #111827", textAlign: "center", verticalAlign: "top" }}>{i + 1}</td>
                    <td style={{ padding: "6px", border: "1px solid #111827", verticalAlign: "top" }}>{item.name}</td>
                    <td style={{ padding: "6px", border: "1px solid #111827", whiteSpace: "pre-line", verticalAlign: "top" }}>{item.description}</td>
                    <td style={{ padding: "6px", border: "1px solid #111827", textAlign: "right", verticalAlign: "top" }}>{(item.amount || 0).toLocaleString()}</td>
                  </tr>
                ))}
                <tr style={{ background: "#F9FAFB" }}>
                  <td colSpan={3} style={{ padding: "6px", border: "1px solid #111827", textAlign: "right", fontWeight: "bold" }}>재료비 합계</td>
                  <td style={{ padding: "6px", border: "1px solid #111827", textAlign: "right", fontWeight: "bold" }}>{(pdfTarget.materialTotal || 0).toLocaleString()} 원</td>
                </tr>
              </tbody>
            </table>

            {/* 인건비 */}
            <h3 style={{ fontSize: "15px", fontWeight: "bold", marginTop: "16px", marginBottom: "6px" }}>■ 인건비</h3>
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "14px", fontSize: "12px" }}>
              <thead>
                <tr style={{ background: "#F3F4F6" }}>
                  <th style={{ padding: "6px", border: "1px solid #111827", width: "7%" }}>No.</th>
                  <th style={{ padding: "6px", border: "1px solid #111827", width: "28%" }}>항목</th>
                  <th style={{ padding: "6px", border: "1px solid #111827" }}>세부 내용</th>
                  <th style={{ padding: "6px", border: "1px solid #111827", width: "22%" }}>금액 (원)</th>
                </tr>
              </thead>
              <tbody>
                {pdfTarget.laborItems?.map((item, i) => (
                  <tr key={i}>
                    <td style={{ padding: "6px", border: "1px solid #111827", textAlign: "center", verticalAlign: "top" }}>{i + 1}</td>
                    <td style={{ padding: "6px", border: "1px solid #111827", verticalAlign: "top" }}>{item.name}</td>
                    <td style={{ padding: "6px", border: "1px solid #111827", whiteSpace: "pre-line", verticalAlign: "top" }}>{item.description}</td>
                    <td style={{ padding: "6px", border: "1px solid #111827", textAlign: "right", verticalAlign: "top" }}>{(item.amount || 0).toLocaleString()}</td>
                  </tr>
                ))}
                <tr style={{ background: "#F9FAFB" }}>
                  <td colSpan={3} style={{ padding: "6px", border: "1px solid #111827", textAlign: "right", fontWeight: "bold" }}>인건비 합계</td>
                  <td style={{ padding: "6px", border: "1px solid #111827", textAlign: "right", fontWeight: "bold" }}>{(pdfTarget.laborTotal || 0).toLocaleString()} 원</td>
                </tr>
              </tbody>
            </table>

            {/* 합계 */}
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "12px", fontSize: "13px" }}>
              <tbody>
                <tr>
                  <td style={{ padding: "8px", border: "1px solid #111827", background: "#F3F4F6", fontWeight: "bold", width: "35%" }}>영업이익 ({pdfTarget.profitRate}%)</td>
                  <td style={{ padding: "8px", border: "1px solid #111827", textAlign: "right" }}>{(pdfTarget.profitAmount || 0).toLocaleString()} 원</td>
                </tr>
                <tr>
                  <td style={{ padding: "8px", border: "1px solid #111827", background: "#F3F4F6", fontWeight: "bold" }}>경비 및 제비용 ({pdfTarget.expenseRate}%)</td>
                  <td style={{ padding: "8px", border: "1px solid #111827", textAlign: "right" }}>{(pdfTarget.expenseAmount || 0).toLocaleString()} 원</td>
                </tr>
                <tr style={{ background: "#FEF3C7" }}>
                  <td style={{ padding: "12px", border: "2px solid #111827", fontWeight: "bold", fontSize: "16px" }}>총 견적 금액 (VAT 별도)</td>
                  <td style={{ padding: "12px", border: "2px solid #111827", textAlign: "right", fontWeight: "bold", fontSize: "18px", color: "#B45309" }}>
                    {(pdfTarget.totalAmount || 0).toLocaleString()} 원
                  </td>
                </tr>
              </tbody>
            </table>

            {pdfTarget.notes && (
              <div style={{ marginTop: "20px", padding: "12px", border: "1px solid #111827", fontSize: "12px" }}>
                <strong>비고:</strong> {pdfTarget.notes}
              </div>
            )}

            <div style={{ marginTop: "28px", textAlign: "center", color: "#9CA3AF", fontSize: "11px" }}>
              본 견적서는 {COMPANY_INFO.name}에서 작성되었습니다.
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
